import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Comment as CommentIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { surface } from '../../theme';
import AnalyticsService, { downloadErrorMessage } from '../../services/AnalyticsService';
import RangePicker from '../../components/analytics/RangePicker';
import DailyMetricChart from '../../components/analytics/DailyMetricChart';
import { Change, ChannelAvatar, INK, Section, StatTile } from '../../components/analytics/AnalyticsParts';
import { ChannelTags, hasRevenue } from '../../components/analytics/ChannelRows';
import { loadSelection, saveSelection, selectionQuery } from '../../utils/analyticsRange';
import { viewerSeesRevenue } from '../../utils/analyticsAccess';
import {
  formatCompact,
  formatCompactMoney,
  formatDate,
  formatDateTime,
  formatDuration,
  formatMoney,
  formatNumber,
  formatPercent,
} from '../../utils/analyticsFormat';

function CommentExportDialog({ channel, range, onClose }) {
  const [scope, setScope] = useState('top');
  const [videos, setVideos] = useState(20);
  const [maxPerVideo, setMaxPerVideo] = useState(200);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function exportComments() {
    setBusy(true);
    setError('');
    try {
      await AnalyticsService.downloadComments(channel.id, {
        scope,
        videos,
        maxPerVideo,
        from: range.from,
        to: range.to,
      });
      onClose();
    } catch (err) {
      setError(await downloadErrorMessage(err, 'Could not export comments.'));
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Export comments</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Download public comments and their replies as a CSV, for reading offline or analyzing
          with another tool.
        </DialogContentText>
        <Stack spacing={2}>
          <TextField select label="Videos" value={scope} onChange={(event) => setScope(event.target.value)}>
            <MenuItem value="top">Most viewed in this range</MenuItem>
            <MenuItem value="recent">Most recently published</MenuItem>
            <MenuItem value="all">All videos</MenuItem>
          </TextField>
          {scope !== 'all' && (
            <TextField
              type="number"
              label="Number of videos"
              value={videos}
              onChange={(event) => setVideos(Number(event.target.value))}
              inputProps={{ min: 1, max: 500 }}
            />
          )}
          <TextField
            type="number"
            label="Comments per video"
            value={maxPerVideo}
            onChange={(event) => setMaxPerVideo(Number(event.target.value))}
            inputProps={{ min: 1, max: 1000 }}
            helperText="Top comments by relevance. Larger exports take longer and use more YouTube quota."
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={exportComments}
          disabled={busy || videos < 1 || maxPerVideo < 1}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
        >
          {busy ? 'Exporting…' : 'Download CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// One video in a list: thumbnail, linked title, a caption line, and its
// numbers. `rank` and `barShare` are optional.
function VideoRow({ video, first, rank, caption, barShare, stats }) {
  const ranked = rank !== undefined;
  return (
    <Box
      component="li"
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '96px minmax(0, 1fr)',
          sm: `${ranked ? '24px ' : ''}128px minmax(0, 1fr) auto`,
        },
        alignItems: 'center',
        columnGap: 1.5,
        rowGap: 1,
        py: 1.25,
        borderTop: first ? 0 : 1,
        borderColor: 'divider',
      }}
    >
      {ranked && (
        <Typography color="text.secondary" sx={{ fontWeight: 600, textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
          {rank}
        </Typography>
      )}
      <Box
        component={video.thumbnailUrl ? 'img' : 'div'}
        src={video.thumbnailUrl || undefined}
        alt=""
        referrerPolicy="no-referrer"
        sx={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 1, bgcolor: 'grey.200' }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Link
          href={`https://www.youtube.com/watch?v=${video.youtubeVideoId}`}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ fontWeight: 600, color: INK, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          <Box component="span" sx={{ overflowWrap: 'anywhere' }}>
            {video.title || 'Unavailable video'}
          </Box>
          <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
        </Link>
        {caption && (
          <Typography variant="caption" color="text.secondary" component="div">
            {caption}
          </Typography>
        )}
        {barShare !== undefined && (
          <Box aria-hidden sx={{ mt: 1, height: 4, borderRadius: 2, bgcolor: 'action.hover', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${barShare * 100}%`, bgcolor: 'primary.main', borderRadius: 2 }} />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          gridColumn: { xs: '1 / -1', sm: 'auto' },
          display: 'grid',
          gridTemplateColumns: `repeat(${stats.length}, auto)`,
          columnGap: 2.5,
          textAlign: { xs: 'left', sm: 'right' },
        }}
      >
        {stats.map(([label, value]) => (
          <Box key={label}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function revenueStats(channel, video) {
  return [
    [channel.revenueShare < 1 ? 'Total revenue' : 'Revenue', formatMoney(video.revenue)],
    ['RPM', formatMoney(video.rpm)],
  ];
}

function TopVideos({ channel, selection }) {
  const [sort, setSort] = useState('revenue');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let current = true;
    setError('');
    AnalyticsService.topVideos(channel.id, selectionQuery(selection), sort)
      .then((result) => current && setData(result))
      .catch((err) => current && setError(err.response?.data?.error || 'Could not load top videos.'));
    return () => {
      current = false;
    };
  }, [channel.id, selection, sort]);

  const videos = data?.videos || [];
  const metric = data?.sort || sort;
  const metricOf = (video) => (metric === 'revenue' ? video.revenue : video.views) || 0;
  const best = Math.max(...videos.map(metricOf), 1);

  return (
    <Section
      title="Top videos"
      action={
        data?.hasRevenue && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={sort}
            onChange={(_, value) => value && setSort(value)}
            aria-label="Rank videos by"
          >
            <ToggleButton value="revenue">By revenue</ToggleButton>
            <ToggleButton value="views">By views</ToggleButton>
          </ToggleButtonGroup>
        )
      }
    >
      {error && <Alert severity="error">{error}</Alert>}
      {!data && !error && <CircularProgress size={24} />}
      {data && !videos.length && (
        <Typography color="text.secondary">
          {channel.source === 'import'
            ? 'Imported channels have channel totals only, not per-video numbers.'
            : 'No video views in this range.'}
        </Typography>
      )}
      <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {videos.map((video, index) => (
          <VideoRow
            key={video.youtubeVideoId}
            video={video}
            first={index === 0}
            rank={index + 1}
            caption={video.publishedAt && `Published ${formatDate(video.publishedAt)}`}
            barShare={metricOf(video) / best}
            stats={[
              ['Views', formatNumber(video.views)],
              ['Avg duration', formatDuration(video.averageViewDurationSeconds)],
              ...(data.hasRevenue ? revenueStats(channel, video) : []),
            ]}
          />
        ))}
      </Box>
    </Section>
  );
}

const RECENT_UPLOADS_SHOWN = 10;
const RECENT_UPLOADS_MORE = 50;

// Calendar days since publishing, on the same UTC dates formatDate shows.
function age(publishedAt) {
  const today = new Date().toISOString().slice(0, 10);
  const days = Math.round(
    (new Date(`${today}T00:00:00Z`) - new Date(`${publishedAt.slice(0, 10)}T00:00:00Z`)) / 86400000,
  );
  if (days < 1) return 'today';
  return days === 1 ? '1 day ago' : `${formatNumber(days)} days ago`;
}

// Whole days until a scheduled time, on local dates.
function timeUntil(scheduledAt) {
  const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const days = Math.round((startOfDay(new Date(scheduledAt)) - startOfDay(new Date())) / 86400000);
  if (days < 1) return 'today';
  return days === 1 ? 'tomorrow' : `in ${formatNumber(days)} days`;
}

function recentCaption(video) {
  if (video.scheduledPublishAt)
    return `Scheduled for ${formatDateTime(video.scheduledPublishAt)} (${timeUntil(video.scheduledPublishAt)})`;
  return [
    `Published ${formatDate(video.publishedAt)} (${age(video.publishedAt)})`,
    video.privacyStatus === 'unlisted' && 'Unlisted',
    video.likeCount !== null && `${formatNumber(video.likeCount)} likes`,
    video.commentCount !== null && `${formatNumber(video.commentCount)} comments`,
  ]
    .filter(Boolean)
    .join(' · ');
}

function latestCheck(videos) {
  const times = videos.map((video) => video.statisticsCheckedAt).filter(Boolean);
  return times.length ? times.reduce((latest, time) => (time > latest ? time : latest)) : null;
}

function recentSubtitle(data, videos) {
  if (!data?.dataThrough) return null;
  const checkedAt = latestCheck(videos);
  return [
    checkedAt && `Views, likes, and comments as of ${formatDateTime(checkedAt)}.`,
    `Average view duration${data.hasRevenue ? ', revenue,' : ''} and first-week views through ${formatDate(data.dataThrough)}.`,
  ]
    .filter(Boolean)
    .join(' ');
}

// The newest uploads with their numbers since publishing. The range picker
// does not apply: each video is measured from its own publish day.
function RecentUploads({ channel }) {
  const [limit, setLimit] = useState(RECENT_UPLOADS_SHOWN);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let current = true;
    setError('');
    AnalyticsService.recentVideos(channel.id, limit)
      .then((result) => current && setData(result))
      .catch((err) => current && setError(err.response?.data?.error || 'Could not load recent uploads.'));
    return () => {
      current = false;
    };
  }, [channel.id, limit]);

  const videos = data?.videos || [];

  return (
    <Section title="Recent uploads" subtitle={recentSubtitle(data, videos)}>
      {error && <Alert severity="error">{error}</Alert>}
      {!data && !error && <CircularProgress size={24} />}
      {data && !videos.length && (
        <Typography color="text.secondary">
          {channel.source === 'import'
            ? 'Imported channels have channel totals only, not per-video numbers.'
            : 'No published or scheduled videos yet.'}
        </Typography>
      )}
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {videos.map((video, index) => (
          <VideoRow
            key={video.youtubeVideoId}
            video={video}
            first={index === 0}
            caption={recentCaption(video)}
            stats={[
              // A scheduled video has no views to show yet.
              ['Views', video.scheduledPublishAt ? '—' : formatNumber(video.viewCount ?? video.views)],
              ['First 7 days', formatNumber(video.firstWeekViews)],
              ['Avg duration', formatDuration(video.averageViewDurationSeconds)],
              ...(data.hasRevenue ? revenueStats(channel, video) : []),
            ]}
          />
        ))}
      </Box>
      {videos.length === limit && limit < RECENT_UPLOADS_MORE && (
        <Button size="small" onClick={() => setLimit(RECENT_UPLOADS_MORE)} sx={{ mt: 1 }}>
          Show more
        </Button>
      )}
    </Section>
  );
}

const UPLOAD_CHANNEL_LABELS = { sleep: 'Sleep', 'ai-stories': 'AI Stories' };

function sourceLabel(channel) {
  if (channel.source === 'import') return 'Imported from YouTube Studio';
  if (channel.uploadConnectionKind) {
    const uploadChannel =
      channel.uploadChannelName ||
      `its ${UPLOAD_CHANNEL_LABELS[channel.uploadConnectionKind]} upload channel`;
    return `Connected through ${uploadChannel}`;
  }
  return 'Connected YouTube channel';
}

// The line beside the channel's revenue: its total when the revenue is split,
// and its expenses and profit once it has expenses in the range. Expenses
// count through today, without the days left out of revenue.
function revenueNote(summary, cut, range) {
  const parts = [
    cut && `Total ${formatMoney(summary.revenue)}`,
    summary.expenses > 0 &&
      `Expenses ${formatMoney(summary.expenses)} through ${formatDate(range.expenses.to)}`,
    summary.expenses > 0 && `Profit ${formatMoney(summary.profit)}`,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

function ChartSection({ title, total, change, note, children }) {
  return (
    <Section
      title={title}
      action={
        total !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 700, fontSize: 18, fontVariantNumeric: 'tabular-nums' }}>
              {total}
            </Typography>
            {change}
            {note && (
              <Typography variant="body2" color="text.secondary">
                {note}
              </Typography>
            )}
          </Box>
        )
      }
    >
      {children}
    </Section>
  );
}

export default function ChannelAnalyticsPage() {
  const { id } = useParams();
  const [selection, setSelection] = useState(loadSelection);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await AnalyticsService.channel(id, selectionQuery(selection)));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load this channel.');
    } finally {
      setLoading(false);
    }
  }, [id, selection]);

  useEffect(() => {
    load();
  }, [load]);

  async function downloadMetrics() {
    setDownloading(true);
    try {
      await AnalyticsService.downloadMetrics(id, data.range.from, data.range.to);
    } catch (err) {
      setError(await downloadErrorMessage(err, 'Could not download the CSV.'));
    } finally {
      setDownloading(false);
    }
  }

  const back = (
    <Button
      component={RouterLink}
      to="/analytics"
      startIcon={<ArrowBackIcon />}
      sx={{ color: 'white', boxShadow: 'none', mb: 1.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
    >
      Analytics
    </Button>
  );

  if (!data) {
    return (
      <Box>
        {back}
        {error && <Alert severity="error">{error}</Alert>}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: 'white' }} />
          </Box>
        )}
      </Box>
    );
  }

  const { channel, summary, previous, range, series } = data;
  const showRevenue = viewerSeesRevenue();
  const revenue = hasRevenue(channel);
  const cut = channel.revenueShare < 1;

  return (
    <Box>
      {back}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <ChannelAvatar channel={channel} size={56} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.2)', overflowWrap: 'anywhere' }}
          >
            {channel.name}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
            {sourceLabel(channel)}
          </Typography>
          <ChannelTags
            channel={{ ...channel, rpm: summary.views ? (summary.revenue / summary.views) * 1000 : null }}
            sx={{ display: 'inline-flex', bgcolor: surface.glass, borderRadius: 2, px: 1, py: 0.5 }}
          />
        </Box>
      </Box>

      <Paper
        sx={{
          borderRadius: 2,
          background: surface.glass,
          boxShadow: 2,
          p: { xs: 2, md: 2.5 },
          mb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <RangePicker
          selection={selection}
          onChange={(next) => {
            saveSelection(next);
            setSelection(next);
          }}
        />
        <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatDate(range.from)} – {formatDate(range.to)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Compared with {formatDate(range.previousFrom)} – {formatDate(range.previousTo)}
            {range.provisional &&
              '. YouTube is still finalizing these days, so the numbers may change.'}
          </Typography>
        </Box>
        <Button
          onClick={downloadMetrics}
          disabled={downloading}
          startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
          sx={{ bgcolor: 'background.paper' }}
        >
          Daily numbers (CSV)
        </Button>
        {channel.connected && (
          <Button startIcon={<CommentIcon />} onClick={() => setCommentsOpen(true)} sx={{ bgcolor: 'background.paper' }}>
            Export comments
          </Button>
        )}
        {showRevenue && (
          <Button
            component={RouterLink}
            to={`/expenses?channel=${channel.id}`}
            startIcon={<ReceiptLongIcon />}
            sx={{ bgcolor: 'background.paper' }}
          >
            Expenses
          </Button>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {channel.problem && <Alert severity="warning" sx={{ mb: 2 }}>{channel.problem}</Alert>}

      <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 150ms' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: `repeat(${showRevenue ? 5 : 4}, minmax(0, 1fr))`,
            },
            gap: 1.5,
            mb: 2,
          }}
        >
          {showRevenue && (
            <StatTile
              label={cut ? 'Your revenue per day' : 'Revenue per day'}
              value={revenue ? formatMoney(cut ? summary.averageRevenueNetPerDay : summary.averageRevenuePerDay) : '—'}
              change={
                revenue && (
                  <Change
                    current={cut ? summary.averageRevenueNetPerDay : summary.averageRevenuePerDay}
                    previous={cut ? previous.averageRevenueNetPerDay : previous.averageRevenuePerDay}
                  />
                )
              }
              footnote={revenue && cut ? `Total ${formatMoney(summary.averageRevenuePerDay)} per day` : null}
            />
          )}
          <StatTile
            label="Views per day"
            value={formatNumber(summary.averageViewsPerDay)}
            change={<Change current={summary.averageViewsPerDay} previous={previous.averageViewsPerDay} />}
          />
          <StatTile
            label="Avg view duration"
            value={formatDuration(summary.averageViewDurationSeconds)}
            change={
              <Change
                current={summary.averageViewDurationSeconds}
                previous={previous.averageViewDurationSeconds}
              />
            }
          />
          <StatTile
            label="Click-through rate"
            value={formatPercent(summary.ctr)}
            change={summary.ctr !== null && <Change points current={summary.ctr} previous={previous.ctr} />}
            footnote={summary.ctr === null ? 'Shown from 10,000 impressions' : null}
          />
          <StatTile
            label="Videos published"
            value={formatNumber(summary.videosUploaded)}
            change={<Change current={summary.videosUploaded} previous={previous.videosUploaded} />}
          />
        </Box>

        {revenue && (
          <ChartSection
            title={cut ? 'Your revenue' : 'Revenue'}
            total={formatMoney(cut ? summary.revenueNet : summary.revenue)}
            change={
              <Change
                compact
                current={cut ? summary.revenueNet : summary.revenue}
                previous={cut ? previous.revenueNet : previous.revenue}
              />
            }
            note={revenueNote(summary, cut, range)}
          >
            <DailyMetricChart
              data={series}
              bucketDays={data.bucketDays}
              value={(day) => (day.revenue === null ? null : day.revenue * channel.revenueShare)}
              format={formatCompactMoney}
              label="Revenue"
              color="#22a06b"
            />
          </ChartSection>
        )}
        <ChartSection
          title="Views"
          total={formatNumber(summary.views)}
          change={<Change compact current={summary.views} previous={previous.views} />}
        >
          <DailyMetricChart
            data={series}
            bucketDays={data.bucketDays}
            value={(day) => day.views}
            format={formatCompact}
            label="Views"
          />
        </ChartSection>
        <ChartSection
          title="Average view duration"
          total={formatDuration(summary.averageViewDurationSeconds)}
        >
          <DailyMetricChart
            data={series}
            bucketDays={data.bucketDays}
            value={(day) => day.averageViewDurationSeconds}
            format={formatDuration}
            label="Avg duration"
            color="#d9822b"
          />
        </ChartSection>

        <TopVideos channel={channel} selection={selection} />
        <RecentUploads channel={channel} />
      </Box>

      {commentsOpen && (
        <CommentExportDialog channel={channel} range={range} onClose={() => setCommentsOpen(false)} />
      )}
    </Box>
  );
}
